<template>
  <div class="operations-container" dir="rtl">
    <vue-element-loading :active="isLoading" spinner="bar-fade-scale" color="#082C69" />

    <!-- ==================== HEADER ==================== -->
    <div class="op-header-row mb-6">
      <div class="op-header-titles">
        <div class="d-flex align-center gap-2 mb-2 flex-wrap">
          <span class="op-badge-section">
            <v-icon x-small color="#082C69" class="me-1">mdi-account-tie-outline</v-icon>
            تعيين السائقين
          </span>

          <!-- Scope Indicator Pill -->
          <div v-if="overview.has_assignments" class="op-scope-pill d-inline-flex align-center">
            <v-icon x-small color="#0284C7" class="me-1">mdi-folder-network-outline</v-icon>
            <span v-if="overview.total_assigned_groups === 1">
              نطاق التشغيل: <strong>{{ overview.groups[0] ? overview.groups[0].group_code + ' — ' + overview.groups[0].name : 'مجموعة واحدة' }}</strong>
            </span>
            <span v-else>
              نطاق التشغيل: <strong>{{ overview.total_assigned_groups }} مجموعات مسندة</strong>
            </span>
          </div>

          <div v-else class="op-no-scope-pill d-inline-flex align-center">
            <v-icon x-small color="#94A3B8" class="me-1">mdi-alert-circle-outline</v-icon>
            <span>لا توجد مجموعات مسندة</span>
          </div>
        </div>

        <h1 class="op-main-title">تعيين سائقي المجموعات</h1>
        <p class="op-subtitle">إدارة وتعيين السائقين لرحلات المجموعات المسندة إليك</p>
      </div>

      <!-- Header Actions -->
      <div class="op-header-actions d-flex align-center gap-2">
        <v-btn
          depressed
          class="op-btn-refresh"
          :loading="isLoading"
          :disabled="isLoading"
          @click="fetchRoutesData"
        >
          <v-icon size="18" color="#082C69">mdi-refresh</v-icon>
          <span class="ms-1 font-weight-bold">تحديث</span>
        </v-btn>
      </div>
    </div>

    <!-- ==================== NO ASSIGNED GROUPS EMPTY STATE ==================== -->
    <div v-if="!isLoading && !overview.has_assignments" class="op-unassigned-card text-center py-12 px-6">
      <div class="op-empty-icon-circle mx-auto mb-4">
        <v-icon size="48" color="#64748B">mdi-folder-alert-outline</v-icon>
      </div>
      <h2 class="op-empty-title mb-2">لا توجد مجموعات تشغيل مسندة إليك حالياً</h2>
      <p class="op-empty-desc mx-auto">
        حسابك التشغيلي نشط، ولكن لم يتم إسناد أي مجموعة تشغيل إليك بعد. يرجى التواصل مع إدارة النظام لتعيين مجموعات التشغيل الخاصة بك لتتمكن من متابعة المسارات والرحلات وتعيين السائقين.
      </p>
    </div>

    <!-- ==================== CONTENT WHEN ASSIGNED ==================== -->
    <div v-else>
      <!-- ==================== TOP FILTER BAR ==================== -->
      <div class="op-filter-bar mb-6">
        <div class="filter-inputs-group">
          <!-- 1. Search Field -->
          <div class="filter-search-box">
            <v-text-field
              v-model="filters.search"
              prepend-inner-icon="mdi-magnify"
              placeholder="البحث برقم الرحلة، كود المجموعة، المسار، السائق..."
              outlined
              dense
              clearable
              hide-details
              class="op-search-input"
              @input="onSearchInput"
              @keydown.enter="onSearchEnter"
              @click:clear="onSearchClear"
            ></v-text-field>
          </div>

          <!-- 2. Date Range Filter -->
          <div class="filter-date-group d-flex align-center gap-2">
            <v-text-field
              v-model="filters.date_from"
              type="date"
              label="من تاريخ"
              outlined
              dense
              hide-details
              class="op-filter-date"
              @change="fetchRoutesData"
            ></v-text-field>
            <v-text-field
              v-model="filters.date_to"
              type="date"
              label="إلى تاريخ"
              outlined
              dense
              hide-details
              class="op-filter-date"
              @change="fetchRoutesData"
            ></v-text-field>
          </div>

          <!-- Quick Date Range Shortcuts -->
          <div class="filter-date-shortcuts d-none d-md-flex align-center gap-1">
            <button
              type="button"
              :class="['op-date-chip', isCurrentWeekActive ? 'chip-active' : '']"
              @click="setCurrentWeekRange"
            >
              هذا الأسبوع
            </button>
            <button
              type="button"
              :class="['op-date-chip', isAllDatesActive ? 'chip-active' : '']"
              @click="setAllDatesRange"
            >
              كل التواريخ
            </button>
          </div>

          <!-- 3. Group Status Filter -->
          <div class="filter-select-box">
            <v-select
              v-model="filters.status"
              :items="statusFilterOptions"
              label="حالة المجموعة"
              outlined
              dense
              hide-details
              class="op-filter-select"
              @change="fetchRoutesData"
            ></v-select>
          </div>

          <!-- 4. Route Filter -->
          <div class="filter-select-box">
            <v-select
              v-model="filters.route_id"
              :items="routeFilterOptions"
              label="المسار"
              outlined
              dense
              hide-details
              class="op-filter-select"
              @change="fetchRoutesData"
            ></v-select>
          </div>
        </div>

        <!-- Filter Actions -->
        <div class="filter-actions-group">
          <button type="button" class="op-filter-btn" @click="resetFilters">
            <v-icon size="16" color="#63738A" class="me-1">mdi-restore</v-icon>
            <span>إعادة تعيين</span>
          </button>
        </div>
      </div>

      <!-- ==================== NO FILTER RESULTS EMPTY STATE ==================== -->
      <div v-if="!isLoading && overview.groups.length === 0" class="op-card text-center py-10 px-4 mb-6">
        <v-icon size="40" color="#94A3B8" class="mb-2">mdi-filter-remove-outline</v-icon>
        <h3 class="font-weight-bold text-navy mb-1">لا توجد نتائج مطابقة</h3>
        <p class="caption text-muted mb-4">لم يتم العثور على مجموعات أو رحلات مطابقة لمعايير البحث والتاريخ الحالية.</p>
        <v-btn small depressed color="#082C69" dark @click="resetFilters">
          إلغاء التصفية
        </v-btn>
      </div>

      <!-- ==================== UNASSIGNED TRIPS ALERT & LIST (REQUIREMENT 1) ==================== -->
      <div
        v-if="overview.unassigned_trips_count && overview.unassigned_trips_count > 0"
        class="op-unassigned-alert-card mb-6"
      >
        <div class="d-flex align-center justify-space-between flex-wrap gap-2 pa-4">
          <div class="d-flex align-center gap-3">
            <div class="op-alert-icon-box">
              <v-icon color="#B45309" size="24">mdi-alert</v-icon>
            </div>
            <div>
              <div class="font-weight-bold" style="color: #92400E; font-size: 15px;">
                تنبيه: يوجد {{ overview.unassigned_trips_count }} رحلة قادمة بحاجة إلى تعيين سائق
              </div>
              <div class="text-caption" style="color: #B45309;">
                يرجى إسناد سائقين ومركبات لهذه الرحلات قبل موعد انطلاقها لضمان سير العمليات بانتظام.
              </div>
            </div>
          </div>

          <v-btn
            depressed
            small
            class="font-weight-bold"
            :style="{ background: showUnassignedDrawer ? '#92400E' : '#FEF3C7', color: showUnassignedDrawer ? '#FFFFFF' : '#92400E', border: '1px solid #FCD34D' }"
            @click="showUnassignedDrawer = !showUnassignedDrawer"
          >
            <v-icon left size="16">{{ showUnassignedDrawer ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            <span>{{ showUnassignedDrawer ? 'إخفاء قائمة الرحلات' : 'عرض قائمة الرحلات غير المعينة (' + overview.unassigned_trips_count + ')' }}</span>
          </v-btn>
        </div>

        <!-- Collapsible List of Unassigned Trips -->
        <v-expand-transition>
          <div v-if="showUnassignedDrawer" class="border-top pa-4" style="background: #FFFDF5;">
            <div class="table-responsive">
              <table class="op-unassigned-table">
                <thead>
                  <tr>
                    <th>معرف الرحلة</th>
                    <th>المجموعة</th>
                    <th>المسار</th>
                    <th>التاريخ</th>
                    <th>التوقيت</th>
                    <th class="text-center" style="min-width: 220px;">تعيين السائق فوراً</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="uTrip in overview.unassigned_trips" :key="`unassigned-${uTrip.id}`">
                    <td class="font-weight-bold">
                      <span class="op-code-tag">{{ uTrip.reference_code }}</span>
                    </td>
                    <td>
                      <span class="text-caption font-weight-bold text-navy">{{ uTrip.group_code }}</span>
                      <small class="d-block text-muted">{{ uTrip.group_name }}</small>
                    </td>
                    <td>
                      <span class="font-weight-bold text-navy">{{ uTrip.route_name }}</span>
                    </td>
                    <td>{{ uTrip.date }}</td>
                    <td>{{ uTrip.time }}</td>
                    <td class="text-center">
                      <v-select
                        :items="getDriverSelectItems(uTrip)"
                        dense
                        outlined
                        hide-details
                        class="op-driver-select"
                        placeholder="اختر سائقاً..."
                        :loading="isAssigning[uTrip.id]"
                        :disabled="isAssigning[uTrip.id]"
                        @change="(driverId) => onDriverChange(uTrip, driverId)"
                      ></v-select>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </v-expand-transition>
      </div>

      <!-- ==================== MAIN GROUPS TABLE (MATCHING REFERENCE) ==================== -->
      <div v-if="overview.groups && overview.groups.length > 0" class="op-main-table-card mb-6">
        <div class="table-responsive">
          <table class="op-groups-table">
            <thead>
              <tr>
                <th style="width: 48px;" class="text-center">#</th>
                <th style="width: 60px;" class="text-center">الرمز</th>
                <th style="width: 130px;">كود المجموعة</th>
                <th>اسم المجموعة</th>
                <th class="text-center" style="width: 140px;">عدد الرحلات</th>
                <th class="text-center" style="width: 130px;">عدد المسارات</th>
                <th class="text-center" style="width: 140px;">حالة المجموعة</th>
                <th class="text-center" style="width: 150px;">الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(group, gIdx) in overview.groups">
                <!-- Group Main Row -->
                <tr
                  :key="`group-row-${group.id}`"
                  :class="['op-group-row', isGroupExpanded(group.id) ? 'row-expanded' : '']"
                  @click="toggleGroupExpand(group.id)"
                >
                  <td class="text-center text-muted font-weight-bold">{{ gIdx + 1 }}</td>
                  <td class="text-center">
                    <div class="op-group-icon-avatar mx-auto">
                      <v-icon size="18" color="#082C69">mdi-folder-network-outline</v-icon>
                    </div>
                  </td>
                  <td>
                    <span class="op-group-code-pill font-monospace font-weight-bold">{{ group.group_code }}</span>
                  </td>
                  <td>
                    <span class="font-weight-bold text-navy text-subtitle-2">{{ group.name }}</span>
                  </td>
                  <td class="text-center">
                    <span class="op-metric-badge metric-trips" :class="{ 'metric-trips-filtered': isGroupFilterActive(group.id) }">
                      <v-icon x-small :color="isGroupFilterActive(group.id) ? '#D97706' : '#082C69'" class="me-1">mdi-bus-clock</v-icon>
                      <template v-if="isGroupFilterActive(group.id)">
                        <strong>{{ getFilteredGroupTrips(group).length }}</strong> من <strong>{{ getTotalGroupTripsCount(group) }}</strong> رحلات
                      </template>
                      <template v-else>
                        <strong>{{ getTotalGroupTripsCount(group) }}</strong> رحلات
                      </template>
                    </span>
                  </td>
                  <td class="text-center">
                    <span class="op-metric-badge metric-routes">
                      <v-icon x-small color="#0284C7" class="me-1">mdi-routes</v-icon>
                      <strong>{{ group.routes_count }}</strong> مسارات
                    </span>
                  </td>
                  <td class="text-center">
                    <span :class="['op-status-badge', getGroupStatusClass(group.status)]">
                      {{ group.status_label }}
                    </span>
                  </td>
                  <td class="text-center" @click.stop>
                    <v-btn
                      small
                      depressed
                      :class="['op-btn-expand', isGroupExpanded(group.id) ? 'btn-expanded' : '']"
                      @click="toggleGroupExpand(group.id)"
                    >
                      <span>عرض التفاصيل</span>
                      <v-icon small class="ms-1 op-chevron-icon">
                        {{ isGroupExpanded(group.id) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                      </v-icon>
                    </v-btn>
                  </td>
                </tr>

                <!-- Nested Expanded Trips Row -->
                <tr
                  v-if="isGroupExpanded(group.id)"
                  :key="`nested-row-${group.id}`"
                  class="op-nested-trips-tr"
                >
                  <td colspan="8" class="pa-0">
                    <div class="op-nested-container py-4 px-5">
                      <!-- Subheader -->
                      <div class="d-flex align-center justify-space-between mb-3 flex-wrap gap-2">
                        <div class="d-flex align-center gap-2 flex-wrap">
                          <v-icon small color="#082C69">mdi-calendar-clock</v-icon>
                          <h3 class="op-nested-title mb-0">
                            رحلات مجموعة <strong>{{ group.name }}</strong>
                          </h3>
                          <span class="op-group-count-pill" :class="{ 'pill-filtered': isGroupFilterActive(group.id) }">
                            <template v-if="isGroupFilterActive(group.id)">
                              {{ getFilteredGroupTrips(group).length }} من {{ getTotalGroupTripsCount(group) }} رحلات
                            </template>
                            <template v-else>
                              {{ getTotalGroupTripsCount(group) }} رحلات
                            </template>
                          </span>
                        </div>
                        <div class="caption text-muted">
                          إمكانية تعيين السائق مباشرة لكل رحلة تشغيلية مجدولة
                        </div>
                      </div>

                      <!-- Per-Group Compact Filter Bar -->
                      <div v-if="getTotalGroupTripsCount(group) > 0" class="op-group-filter-bar mb-3">
                        <div class="op-group-filter-inputs">
                          <!-- A) Date Filter -->
                          <div class="op-gf-item op-gf-date">
                            <v-text-field
                              :value="getGroupFilter(group.id).date"
                              type="date"
                              label="التاريخ"
                              placeholder="كل التواريخ"
                              outlined
                              dense
                              clearable
                              hide-details
                              class="op-gf-control"
                              @input="(val) => updateGroupFilter(group.id, 'date', val || '')"
                              @click:clear="updateGroupFilter(group.id, 'date', '')"
                            ></v-text-field>
                          </div>

                          <!-- B) Route Filter (scoped to routes inside THIS group) -->
                          <div class="op-gf-item op-gf-route">
                            <v-autocomplete
                              :value="getGroupFilter(group.id).route_id"
                              :items="getGroupRouteOptions(group)"
                              item-text="text"
                              item-value="value"
                              label="المسار"
                              outlined
                              dense
                              hide-details
                              no-data-text="لا توجد مسارات مطابقة"
                              class="op-gf-control"
                              @change="(val) => updateGroupFilter(group.id, 'route_id', val || 'all')"
                            ></v-autocomplete>
                          </div>

                          <!-- C) Assignment Status Filter -->
                          <div class="op-gf-item op-gf-status">
                            <v-select
                              :value="getGroupFilter(group.id).status"
                              :items="getGroupStatusOptions(group)"
                              item-text="text"
                              item-value="value"
                              label="حالة التعيين"
                              outlined
                              dense
                              hide-details
                              class="op-gf-control"
                              @change="(val) => updateGroupFilter(group.id, 'status', val || 'all')"
                            ></v-select>
                          </div>
                        </div>

                        <div class="op-group-filter-actions d-flex align-center gap-2">
                          <button
                            type="button"
                            class="op-gf-reset-btn"
                            :class="{ 'is-active': isGroupFilterActive(group.id) }"
                            @click="resetGroupFilter(group.id)"
                          >
                            <v-icon size="15" :color="isGroupFilterActive(group.id) ? '#082C69' : '#64748B'" class="me-1">mdi-restore</v-icon>
                            <span>إعادة تعيين</span>
                          </button>
                        </div>
                      </div>

                      <!-- Nested Trips Table -->
                      <div v-if="getFilteredGroupTrips(group).length > 0" class="table-responsive border rounded-lg bg-white">
                        <table class="op-trips-table">
                          <thead>
                            <tr>
                              <th style="width: 40px;" class="text-center">#</th>
                              <th style="width: 130px;">كود الرحلة</th>
                              <th>المسار</th>
                              <th style="width: 160px;">التاريخ</th>
                              <th style="width: 100px;">الانطلاق</th>
                              <th style="width: 100px;">الوصول</th>
                              <th class="text-center" style="width: 120px;">الحالة</th>
                              <th style="width: 250px;">السائق المسند</th>
                              <th style="width: 170px;">تسعيرة السائق</th>
                              <th class="text-center" style="width: 60px;">عرض</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(trip, tIdx) in getFilteredGroupTrips(group)" :key="trip.id" class="op-trip-item-row">
                              <td class="text-center text-muted font-weight-medium">{{ tIdx + 1 }}</td>
                              <td>
                                <div class="d-flex flex-column">
                                  <span class="op-trip-code-pill font-monospace font-weight-bold">{{ trip.planned_trip_code }}</span>
                                  <span class="op-parent-ref caption text-muted mt-0.5">أساسي: {{ trip.parent_trip_code }}</span>
                                </div>
                              </td>
                              <td>
                                <div class="d-flex flex-column">
                                  <span class="font-weight-bold text-navy">{{ trip.route_name }}</span>
                                  <span class="op-route-code-pill font-monospace mt-0.5">{{ trip.route_code }}</span>
                                </div>
                              </td>
                              <td>
                                <span class="caption font-weight-bold text-navy">{{ trip.planned_date_formatted || trip.planned_date || '—' }}</span>
                              </td>
                              <td>
                                <span class="caption font-weight-bold text-navy">{{ trip.first_stop_time_formatted || trip.first_stop_time || '—' }}</span>
                              </td>
                              <td>
                                <span class="caption font-weight-bold text-navy">{{ trip.last_stop_time_formatted || trip.last_stop_time || '—' }}</span>
                              </td>
                              <td class="text-center">
                                <span :class="['op-trip-status-pill', getTripStatusClass(trip.status)]">
                                  {{ trip.status_label }}
                                </span>
                              </td>
                              <!-- Inline Driver Assignment Dropdown -->
                              <td>
                                <div class="d-flex align-center gap-2 op-driver-select-cell">
                                  <div class="position-relative flex-grow-1">
                                    <v-select
                                      :value="trip.driver_id"
                                      :items="getDriverSelectItems(trip)"
                                      item-text="text"
                                      item-value="value"
                                      outlined
                                      dense
                                      hide-details
                                      :disabled="!trip.can_assign || isAssigning[trip.id]"
                                      :loading="isAssigning[trip.id]"
                                      placeholder="اختر سائقاً..."
                                      class="op-driver-select"
                                      @change="(newVal) => onDriverSelectChange(trip, newVal, group)"
                                    >
                                      <template v-slot:prepend-inner>
                                        <v-icon x-small color="#082C69" class="me-1">mdi-account</v-icon>
                                      </template>
                                    </v-select>
                                  </div>

                                  <!-- Tooltip explaining why disabled if locked -->
                                  <v-tooltip v-if="!trip.can_assign" bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-icon x-small color="#94A3B8" v-bind="attrs" v-on="on">
                                        mdi-lock-outline
                                      </v-icon>
                                    </template>
                                    <span>لا يمكن تعديل سائق رحلة {{ trip.status_label }}</span>
                                  </v-tooltip>
                                </div>
                              </td>
                              <!-- Inline Driver Fare Field -->
                              <td>
                                <div class="op-fare-cell d-flex align-center gap-1">
                                  <div class="position-relative flex-grow-1">
                                    <div
                                      class="op-fare-input-box"
                                      :class="{
                                        'is-disabled': !trip.can_edit_fare,
                                        'is-loading': isSavingFare[trip.id]
                                      }"
                                    >
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        :value="getFareValue(trip)"
                                        :disabled="!trip.can_edit_fare || isSavingFare[trip.id]"
                                        placeholder="0.00"
                                        class="op-fare-input"
                                        @input="(e) => onFareInput(trip, e.target.value)"
                                        @blur="saveDriverFare(trip, group)"
                                        @keyup.enter="$event.target.blur()"
                                      />
                                      <span class="op-fare-currency">جنيه</span>
                                      <v-progress-circular
                                        v-if="isSavingFare[trip.id]"
                                        indeterminate
                                        size="14"
                                        width="2"
                                        color="#082C69"
                                        class="op-fare-spinner"
                                      ></v-progress-circular>
                                    </div>
                                  </div>

                                  <!-- Tooltip if read-only / locked -->
                                  <v-tooltip v-if="!trip.can_edit_fare" bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-icon x-small color="#94A3B8" v-bind="attrs" v-on="on">
                                        mdi-lock-outline
                                      </v-icon>
                                    </template>
                                    <span>لا يمكن تعديل تسعيرة رحلة {{ trip.status_label }}</span>
                                  </v-tooltip>
                                </div>
                              </td>
                              <td class="text-center">
                                <v-tooltip bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                      icon
                                      small
                                      v-bind="attrs"
                                      v-on="on"
                                      class="op-action-btn"
                                      @click="openTripDetails(trip)"
                                    >
                                      <v-icon small color="#082C69">mdi-eye-outline</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>عرض تفاصيل ومسار الرحلة</span>
                                </v-tooltip>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Empty State When Group Filters Match 0 Trips -->
                      <div
                        v-else-if="getTotalGroupTripsCount(group) > 0"
                        class="text-center py-6 bg-white border rounded-lg text-muted"
                      >
                        <v-icon size="26" color="#F59E0B" class="d-block mb-1 mx-auto">mdi-filter-off-outline</v-icon>
                        <div class="body-2 font-weight-bold text-navy mb-1">
                          لا توجد رحلات مطابقة لمعايير التصفية داخل هذه المجموعة (0 من {{ getTotalGroupTripsCount(group) }} رحلات)
                        </div>
                        <button type="button" class="op-gf-reset-btn is-active mt-2" @click="resetGroupFilter(group.id)">
                          <v-icon size="14" color="#082C69" class="me-1">mdi-restore</v-icon>
                          <span>إعادة تعيين تصفية المجموعة</span>
                        </button>
                      </div>

                      <!-- Empty State Inside Expanded Group -->
                      <div v-else class="text-center py-8 bg-white border rounded-lg text-muted">
                        <v-icon size="28" color="#94A3B8" class="d-block mb-1">mdi-bus-clock</v-icon>
                        <span class="body-2">لا توجد رحلات مجدولة لهذه المجموعة حالياً</span>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ==================== VIEW-ONLY TRIP DETAILS MODAL ==================== -->
    <v-dialog v-model="tripDetailsDialog" max-width="850px" scrollable>
      <v-card v-if="selectedTripDetails" class="op-dialog-card" dir="rtl">
        <!-- Dialog Header -->
        <v-card-title class="op-dialog-header d-flex align-center justify-space-between py-4 px-6">
          <div class="d-flex align-center gap-2 flex-wrap">
            <div class="op-icon-header-box me-2">
              <v-icon size="20" color="#082C69">mdi-bus</v-icon>
            </div>
            <div>
              <div class="d-flex align-center gap-2">
                <span class="op-trip-code-pill font-monospace font-weight-bold">{{ selectedTripDetails.trip.planned_trip_code || selectedTripDetails.trip.trip_code }}</span>
                <span class="op-route-code-pill font-monospace">{{ selectedTripDetails.route.route_code }}</span>
                <span :class="['op-trip-status-pill', getTripStatusClass(selectedTripDetails.trip.status)]">
                  {{ selectedTripDetails.trip.status_label }}
                </span>
              </div>
              <h3 class="op-dialog-title mb-0 mt-1">{{ selectedTripDetails.route.name }}</h3>
            </div>
          </div>
          <v-btn icon small @click="tripDetailsDialog = false">
            <v-icon color="#64748B">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <!-- 4 Stat Cards Grid -->
          <div class="op-trip-modal-grid mb-5">
            <div class="op-modal-stat-card">
              <span class="stat-label">التاريخ</span>
              <span class="stat-value text-navy">{{ selectedTripDetails.trip.planned_date_formatted || '—' }}</span>
            </div>
            <div class="op-modal-stat-card">
              <span class="stat-label">وقت الانطلاق</span>
              <span class="stat-value text-navy">{{ selectedTripDetails.trip.first_stop_time_formatted }}</span>
            </div>
            <div class="op-modal-stat-card">
              <span class="stat-label">وقت الوصول المتوقع</span>
              <span class="stat-value text-navy">{{ selectedTripDetails.trip.last_stop_time_formatted }}</span>
            </div>
            <div class="op-modal-stat-card">
              <span class="stat-label">السائق المسند</span>
              <span class="stat-value text-emerald font-weight-bold">
                {{ selectedTripDetails.trip.driver ? selectedTripDetails.trip.driver.name : 'قيد التعيين' }}
              </span>
            </div>
            <div class="op-modal-stat-card">
              <span class="stat-label">سعر الرحلة (للراكب)</span>
              <span class="stat-value text-navy font-weight-bold">
                {{ (selectedTripDetails.trip && selectedTripDetails.trip.passenger_fare_formatted) ? `${selectedTripDetails.trip.passenger_fare_formatted} جنيه` : ((selectedTripDetails.route && selectedTripDetails.route.passenger_fare_formatted) ? `${selectedTripDetails.route.passenger_fare_formatted} جنيه` : 'غير محدد') }}
              </span>
            </div>
            <div class="op-modal-stat-card">
              <span class="stat-label">تسعيرة السائق</span>
              <span class="stat-value text-navy font-weight-bold">
                {{ selectedTripDetails.trip.driver_fare_formatted ? `${selectedTripDetails.trip.driver_fare_formatted} جنيه` : 'غير محددة' }}
              </span>
            </div>
          </div>

          <!-- Stops Timetable -->
          <h4 class="font-weight-bold text-navy mb-3 d-flex align-center">
            <v-icon small color="#082C69" class="me-1">mdi-map-marker-path</v-icon>
            محطات المسار ومواعيد الوصول المقدرة ({{ selectedTripDetails.stops.length }} محطة)
          </h4>

          <div class="table-responsive border rounded-lg mb-5">
            <table class="op-trips-table">
              <thead>
                <tr>
                  <th style="width: 40px;" class="text-center">#</th>
                  <th>اسم المحطة</th>
                  <th>العنوان</th>
                  <th>وقت الوصول (ETA)</th>
                  <th class="text-center">زمن الطريق (دقيقة)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="stop in selectedTripDetails.stops" :key="stop.order">
                  <td class="text-center text-muted font-weight-bold">{{ stop.order }}</td>
                  <td class="font-weight-bold text-navy">{{ stop.name }}</td>
                  <td class="caption text-muted">{{ stop.address || '—' }}</td>
                  <td class="caption font-weight-bold text-emerald">{{ stop.eta_formatted || '—' }}</td>
                  <td class="text-center caption">{{ stop.road_minutes_from_previous || '0' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Google Map View -->
          <div v-if="selectedTripDetails.map && selectedTripDetails.map.markers.length > 0" class="mt-4">
            <h4 class="font-weight-bold text-navy mb-2 d-flex align-center">
              <v-icon small color="#082C69" class="me-1">mdi-map-outline</v-icon>
              خريطة المسار والمحطات
            </h4>
            <div class="op-modal-map-container">
              <GoogleMapLoader
                :apiKey="googleMapsApiKey"
                :mapConfig="mapConfig"
                :center="mapCenter"
                :zoom="12"
                :markers="mapMarkers"
                :polylines="mapPolylines"
                class="op-map-element"
              />
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4 bg-slate-50 justify-end">
          <v-btn depressed class="op-btn-secondary" @click="tripDetailsDialog = false">
            إغلاق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { Keys } from "@/config.js";
import GoogleMapLoader from "@/components/GoogleMapLoader.vue";

export default {
  name: "OperationsRoutes",
  components: {
    GoogleMapLoader,
  },
  data() {
    return {
      isLoading: false,
      overview: {
        has_assignments: false,
        total_assigned_groups: 0,
        groups: [],
        eligible_drivers: [],
        filter_options: {
          groups: [],
          routes: [],
          statuses: [],
        },
      },
      filters: {
        search: "",
        group_id: "all",
        route_id: "all",
        status: "all",
        date_from: "",
        date_to: "",
      },
      searchDebounceTimer: null,
      expandedGroups: [], // Array of expanded group IDs (all collapsed by default)
      showUnassignedDrawer: false,
      groupFilters: {}, // Scoped per-group filter state: { [groupId]: { date: '', route_id: 'all', status: 'all' } }
      isAssigning: {}, // Map of trip.id -> boolean for loading indicator
      isSavingFare: {}, // Map of trip.id -> boolean for fare saving
      fareDrafts: {}, // Map of trip.id -> string/number draft while typing
      hasInitializedExpansion: false,
      // Trip Details Modal
      tripDetailsDialog: false,
      selectedTripDetails: null,
      googleMapsApiKey: Keys.GOOGLE_MAPS_API_KEY || "",
      mapConfig: {
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
      },
    };
  },
  computed: {
    routeFilterOptions() {
      const opts = [{ value: "all", text: "كل المسارات" }];
      if (this.overview.filter_options && this.overview.filter_options.routes) {
        this.overview.filter_options.routes.forEach((r) => {
          opts.push({ value: r.id, text: r.label || r.name });
        });
      }
      return opts;
    },
    statusFilterOptions() {
      const opts = [{ value: "all", text: "كل الحالات" }];
      if (this.overview.filter_options && this.overview.filter_options.statuses) {
        this.overview.filter_options.statuses.forEach((s) => {
          if (s.value !== "all") {
            opts.push({ value: s.value, text: s.label });
          }
        });
      }
      return opts;
    },
    isAllDatesActive() {
      return this.filters.date_from === "" && this.filters.date_to === "";
    },
    isCurrentWeekActive() {
      return this.filters.date_from !== "" && this.filters.date_to !== "";
    },
    mapCenter() {
      if (
        this.selectedTripDetails &&
        this.selectedTripDetails.map &&
        this.selectedTripDetails.map.markers &&
        this.selectedTripDetails.map.markers.length > 0
      ) {
        return this.selectedTripDetails.map.markers[0].position;
      }
      return { lat: 30.0444, lng: 31.2357 };
    },
    mapMarkers() {
      if (
        this.selectedTripDetails &&
        this.selectedTripDetails.map &&
        this.selectedTripDetails.map.markers
      ) {
        return this.selectedTripDetails.map.markers;
      }
      return [];
    },
    mapPolylines() {
      if (
        this.selectedTripDetails &&
        this.selectedTripDetails.map &&
        this.selectedTripDetails.map.polyline &&
        this.selectedTripDetails.map.polyline.length > 0
      ) {
        return [
          {
            data: this.selectedTripDetails.map.polyline,
            strokeColor: "#082C69",
            strokeWeight: 5,
            strokeOpacity: 0.9,
          },
        ];
      }
      return [];
    },
  },
  created() {
    this.fetchRoutesData();
  },
  methods: {
    getDefaultGroupFilter() {
      return {
        date: "",
        route_id: "all",
        status: "all",
      };
    },
    getGroupFilter(groupId) {
      if (!this.groupFilters[groupId]) {
        return this.getDefaultGroupFilter();
      }
      return this.groupFilters[groupId];
    },
    updateGroupFilter(groupId, field, value) {
      const current = this.groupFilters[groupId]
        ? { ...this.groupFilters[groupId] }
        : this.getDefaultGroupFilter();
      current[field] = value;
      this.$set(this.groupFilters, groupId, current);
    },
    resetGroupFilter(groupId) {
      this.$set(this.groupFilters, groupId, this.getDefaultGroupFilter());
    },
    isGroupFilterActive(groupId) {
      const gf = this.groupFilters[groupId];
      if (!gf) return false;
      return (
        Boolean(gf.date && gf.date !== "") ||
        Boolean(gf.route_id && gf.route_id !== "all") ||
        Boolean(gf.status && gf.status !== "all")
      );
    },
    getTotalGroupTripsCount(group) {
      if (group && Array.isArray(group.planned_trips)) {
        return group.planned_trips.length;
      }
      return group && group.trips_count ? group.trips_count : 0;
    },
    getFilteredGroupTrips(group) {
      const trips = group && Array.isArray(group.planned_trips) ? group.planned_trips : [];
      const gf = this.groupFilters[group.id];
      if (!gf || !this.isGroupFilterActive(group.id)) {
        return trips;
      }

      return trips.filter((trip) => {
        if (gf.date && gf.date !== "") {
          if (String(trip.planned_date || "") !== String(gf.date)) {
            return false;
          }
        }
        if (gf.route_id && gf.route_id !== "all") {
          if (Number(trip.route_id) !== Number(gf.route_id)) {
            return false;
          }
        }
        if (gf.status && gf.status !== "all") {
          if (String(trip.status || "") !== String(gf.status)) {
            return false;
          }
        }
        return true;
      });
    },
    getGroupRouteOptions(group) {
      const opts = [{ value: "all", text: "كل المسارات" }];
      const seen = new Set();

      if (group && Array.isArray(group.routes)) {
        group.routes.forEach((r) => {
          if (r && r.id && !seen.has(Number(r.id))) {
            seen.add(Number(r.id));
            opts.push({
              value: Number(r.id),
              text: r.label || (r.code ? `${r.code} - ${r.name}` : r.name),
            });
          }
        });
      }

      if (group && Array.isArray(group.planned_trips)) {
        group.planned_trips.forEach((pt) => {
          if (pt && pt.route_id && !seen.has(Number(pt.route_id))) {
            seen.add(Number(pt.route_id));
            opts.push({
              value: Number(pt.route_id),
              text: pt.route_code ? `${pt.route_code} - ${pt.route_name}` : pt.route_name,
            });
          }
        });
      }

      return opts;
    },
    getGroupStatusOptions(group) {
      const opts = [
        { value: "all", text: "الكل" },
        { value: "assigned", text: "تم التعيين" },
        { value: "unassigned", text: "بدون سائق / قيد التعيين" },
      ];
      const existingValues = new Set(["all", "assigned", "unassigned"]);

      if (group && Array.isArray(group.planned_trips)) {
        group.planned_trips.forEach((pt) => {
          if (pt && pt.status && !existingValues.has(pt.status)) {
            existingValues.add(pt.status);
            opts.push({
              value: pt.status,
              text: pt.status_label || pt.status,
            });
          }
        });
      }

      return opts;
    },
    setDefaultDateRange() {
      // Default to 7-day operational window covering current active dates
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - 5);
      const end = new Date(now);
      end.setDate(now.getDate() + 6);

      this.filters.date_from = this.formatDateIso(start);
      this.filters.date_to = this.formatDateIso(end);
    },
    formatDateIso(d) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    setCurrentWeekRange() {
      this.setDefaultDateRange();
      this.fetchRoutesData();
    },
    setAllDatesRange() {
      this.filters.date_from = "";
      this.filters.date_to = "";
      this.fetchRoutesData();
    },
    fetchRoutesData() {
      this.isLoading = true;
      const params = {};
      if (this.filters.search) params.search = this.filters.search;
      if (this.filters.group_id && this.filters.group_id !== "all") params.group_id = this.filters.group_id;
      if (this.filters.route_id && this.filters.route_id !== "all") params.route_id = this.filters.route_id;
      if (this.filters.status && this.filters.status !== "all") params.status = this.filters.status;
      if (this.filters.date_from) params.date_from = this.filters.date_from;
      if (this.filters.date_to) params.date_to = this.filters.date_to;

      axios
        .get("/operations/routes", { params })
        .then((response) => {
          if (response.data && response.data.success) {
            this.overview = response.data.data;

            if (this.overview.groups && this.overview.groups.length > 0) {
              if (this.filters.search && this.filters.search.trim().length > 0) {
                // Auto-expand all matched groups so the user immediately sees the results
                this.expandedGroups = this.overview.groups.map(g => g.id);
              } else if (!this.hasInitializedExpansion) {
                // Requirement 3: All Groups begin collapsed by default. Do NOT auto-expand the first group!
                this.expandedGroups = [];
                this.hasInitializedExpansion = true;
              }
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching operations routes:", error);
          this.notifyUser("error", "خطأ", "تعذر تحميل بيانات المسارات والمجموعات.");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    isGroupExpanded(groupId) {
      return this.expandedGroups.includes(groupId);
    },
    toggleGroupExpand(groupId) {
      const index = this.expandedGroups.indexOf(groupId);
      if (index >= 0) {
        this.expandedGroups.splice(index, 1);
      } else {
        this.expandedGroups.push(groupId);
      }
    },
    getDriverSelectItems(trip) {
      const items = [{ value: null, text: "بدون سائق (إلغاء التعيين)" }];
      const eligible = this.overview.eligible_drivers || [];

      eligible.forEach((d) => {
        items.push({
          value: d.id,
          text: d.label || d.name,
        });
      });

      // If current driver is set but not in eligible list, prepend him so select renders correctly
      if (trip.driver_id && !items.some((i) => i.value === trip.driver_id)) {
        items.splice(1, 0, {
          value: trip.driver_id,
          text: trip.driver ? `${trip.driver.name} (حالي)` : `سائق #${trip.driver_id}`,
        });
      }

      return items;
    },
    onDriverSelectChange(trip, newDriverId, group) {
      if (trip.driver_id === newDriverId) return;

      const previousDriverId = trip.driver_id;
      const previousDriver = trip.driver;
      const previousStatus = trip.status;
      const previousStatusLabel = trip.status_label;

      this.$set(this.isAssigning, trip.id, true);

      axios
        .post(`/operations/routes/planned-trips/${trip.id}/assign-driver`, {
          planned_trip_id: trip.planned_trip_id || trip.id,
          driver_id: newDriverId,
        })
        .then((res) => {
          if (res.data && res.data.success) {
            const updatedTrip = res.data.trip || {};
            // Safely merge into the existing trip inside group.planned_trips
            const idx = group.planned_trips.findIndex((t) => t.id === trip.id);
            if (idx !== -1) {
              const existing = group.planned_trips[idx];
              const merged = {
                ...existing,
                ...updatedTrip,
                planned_date: updatedTrip.planned_date || existing.planned_date,
                planned_date_formatted: updatedTrip.planned_date_formatted || existing.planned_date_formatted,
                first_stop_time: updatedTrip.first_stop_time || existing.first_stop_time,
                first_stop_time_formatted: updatedTrip.first_stop_time_formatted || existing.first_stop_time_formatted,
                last_stop_time: updatedTrip.last_stop_time || existing.last_stop_time,
                last_stop_time_formatted: updatedTrip.last_stop_time_formatted || existing.last_stop_time_formatted,
                expected_duration: updatedTrip.expected_duration || existing.expected_duration,
                duration_minutes: updatedTrip.duration_minutes !== undefined ? updatedTrip.duration_minutes : existing.duration_minutes,
                route_name: updatedTrip.route_name || existing.route_name,
                route_code: updatedTrip.route_code || existing.route_code,
                planned_trip_code: updatedTrip.planned_trip_code || existing.planned_trip_code,
                parent_trip_code: updatedTrip.parent_trip_code || existing.parent_trip_code,
              };
              this.$set(group.planned_trips, idx, merged);
            }
            this.notifyUser("success", "تم بنجاح", res.data.message || "تم تحديث تعيين السائق للرحلة.");
          } else {
            throw new Error(res.data.message || "تعذر تعيين السائق.");
          }
        })
        .catch((err) => {
          console.error("Assignment error:", err);
          // Revert in-place
          trip.driver_id = previousDriverId;
          trip.driver = previousDriver;
          trip.status = previousStatus;
          trip.status_label = previousStatusLabel;

          const errMsg =
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : err.message || "تعذر تعيين السائق بسبب تعارض أو خطأ في النظام.";
          this.notifyUser("error", "تعذر تعيين السائق", errMsg);
        })
        .finally(() => {
          this.$set(this.isAssigning, trip.id, false);
        });
    },
    getFareValue(trip) {
      if (this.fareDrafts[trip.id] !== undefined) {
        return this.fareDrafts[trip.id];
      }
      if (trip.driver_fare !== null && trip.driver_fare !== undefined) {
        return trip.driver_fare;
      }
      return "";
    },
    onFareInput(trip, value) {
      this.$set(this.fareDrafts, trip.id, value);
    },
    saveDriverFare(trip, group) {
      const rawVal = this.fareDrafts[trip.id] !== undefined ? this.fareDrafts[trip.id] : trip.driver_fare;

      let fareToSend = null;
      if (rawVal !== "" && rawVal !== null && rawVal !== undefined) {
        const parsed = parseFloat(rawVal);
        if (isNaN(parsed) || parsed < 0) {
          this.notifyUser("error", "خطأ في القيمة", "يرجى إدخال قيمة صحيحة وموجبة لتسعيرة السائق.");
          return;
        }
        fareToSend = Math.round(parsed * 100) / 100;
      }

      // Check if unchanged
      const currentVal =
        trip.driver_fare !== null && trip.driver_fare !== undefined
          ? parseFloat(trip.driver_fare)
          : null;
      if (fareToSend === currentVal) {
        this.$delete(this.fareDrafts, trip.id);
        return;
      }

      this.$set(this.isSavingFare, trip.id, true);

      axios
        .put(`/operations/routes/planned-trips/${trip.id}/driver-fare`, {
          driver_fare: fareToSend,
        })
        .then((res) => {
          if (res.data && res.data.success) {
            const updatedTrip = res.data.trip || {};
            const idx = group.planned_trips.findIndex((t) => t.id === trip.id);
            if (idx !== -1) {
              const existing = group.planned_trips[idx];
              const merged = {
                ...existing,
                ...updatedTrip,
                planned_date: updatedTrip.planned_date || existing.planned_date,
                planned_date_formatted: updatedTrip.planned_date_formatted || existing.planned_date_formatted,
                first_stop_time: updatedTrip.first_stop_time || existing.first_stop_time,
                first_stop_time_formatted: updatedTrip.first_stop_time_formatted || existing.first_stop_time_formatted,
                last_stop_time: updatedTrip.last_stop_time || existing.last_stop_time,
                last_stop_time_formatted: updatedTrip.last_stop_time_formatted || existing.last_stop_time_formatted,
              };
              this.$set(group.planned_trips, idx, merged);
            }
            this.$delete(this.fareDrafts, trip.id);
            this.notifyUser("success", "تم الحفظ", res.data.message || "تم حفظ تسعيرة السائق بنجاح.");
          } else {
            throw new Error(res.data.message || "تعذر حفظ تسعيرة السائق.");
          }
        })
        .catch((err) => {
          console.error("Error saving driver fare:", err);
          const errMsg =
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : err.message || "تعذر حفظ تسعيرة السائق.";
          this.notifyUser("error", "تعذر حفظ التسعيرة", errMsg);
          this.$delete(this.fareDrafts, trip.id);
        })
        .finally(() => {
          this.$set(this.isSavingFare, trip.id, false);
        });
    },
    onSearchInput() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.fetchRoutesData();
      }, 350);
    },
    onSearchEnter() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.fetchRoutesData();
    },
    onSearchClear() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.filters.search = "";
      this.$nextTick(() => {
        this.fetchRoutesData();
      });
    },
    resetFilters() {
      this.filters = {
        search: "",
        group_id: "all",
        route_id: "all",
        status: "all",
        date_from: "",
        date_to: "",
      };
      this.fetchRoutesData();
    },
    openTripDetails(trip) {
      this.isLoading = true;
      axios
        .get(`/operations/routes/trips/${trip.id}`)
        .then((res) => {
          if (res.data && res.data.success) {
            this.selectedTripDetails = res.data.data;
            this.tripDetailsDialog = true;
          }
        })
        .catch((err) => {
          console.error("Error loading trip details:", err);
          const msg =
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : "تعذر تحميل تفاصيل الرحلة.";
          this.notifyUser("error", "خطأ", msg);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getGroupStatusClass(status) {
      if (status === "published") return "badge-published";
      if (status === "draft") return "badge-draft";
      return "badge-inactive";
    },
    getTripStatusClass(status) {
      if (status === "assigned") return "status-assigned";
      if (status === "unassigned") return "status-unassigned";
      if (status === "running") return "status-running";
      if (status === "completed") return "status-completed";
      if (status === "cancelled") return "status-cancelled";
      return "status-unassigned";
    },
    notifyUser(type, title, text) {
      if (this.$notify) {
        this.$notify({
          title: title || (type === "success" ? "تم بنجاح" : "خطأ"),
          text: text,
          type: type === "warn" ? "warn" : type,
        });
      } else if (this.$swal) {
        this.$swal(title || (type === "success" ? "تم بنجاح" : "خطأ"), text, type === "warn" ? "warning" : type);
      } else {
        alert(text);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.operations-container {
  font-family: inherit;
  color: #1E293B;
}

/* Header */
.op-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.op-badge-section {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background-color: #EEF2F6;
  color: #082C69;
  font-size: 12px;
  font-weight: 700;
  border-radius: 8px;
}

.op-scope-pill {
  padding: 3px 10px;
  background-color: #F0F9FF;
  border: 1px solid #BAE6FD;
  border-radius: 20px;
  color: #0369A1;
  font-size: 11px;
}

.op-no-scope-pill {
  padding: 3px 10px;
  background-color: #F1F5F9;
  border: 1px solid #CBD5E1;
  border-radius: 20px;
  color: #64748B;
  font-size: 11px;
}

.op-main-title {
  font-size: 24px;
  font-weight: 800;
  color: #082C69;
  letter-spacing: -0.5px;
  margin: 0;
}

.op-subtitle {
  font-size: 13px;
  color: #64748B;
  margin: 4px 0 0 0;
}

.op-btn-refresh {
  height: 40px !important;
  background: #FFFFFF !important;
  border: 1px solid #E2E8F0 !important;
  border-radius: 10px !important;
  color: #082C69 !important;
  padding: 0 16px !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;
}

/* Filter Bar */
.op-filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  gap: 12px;
  flex-wrap: wrap;
}

.filter-inputs-group {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-grow: 1;
  flex-wrap: wrap;
}

.filter-search-box {
  flex-grow: 2;
  min-width: 240px;
}

.filter-date-group {
  min-width: 250px;
}

.op-filter-date {
  max-width: 140px;
}

.filter-date-shortcuts {
  margin-right: 4px;
}

.op-date-chip {
  padding: 6px 12px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  color: #475569;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #F1F5F9;
    border-color: #CBD5E1;
  }

  &.chip-active {
    background: #EEF2F6;
    color: #082C69;
    border-color: #082C69;
  }
}

.filter-select-box {
  flex-grow: 1;
  min-width: 160px;
}

.filter-actions-group {
  display: flex;
  align-items: center;
}

.op-filter-btn {
  display: inline-flex;
  align-items: center;
  height: 38px;
  padding: 0 14px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #F1F5F9;
    border-color: #CBD5E1;
  }
}

/* Main Groups Table Card */
.op-main-table-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.op-groups-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.op-groups-table th {
  padding: 14px 16px;
  background-color: #F8FAFC;
  color: #475569;
  font-weight: 700;
  text-align: right;
  border-bottom: 1px solid #E2E8F0;
  white-space: nowrap;
}

.op-group-row {
  cursor: pointer;
  transition: all 0.15s ease;
  border-bottom: 1px solid #E2E8F0;

  &:hover {
    background-color: #F8FAFC;
  }

  &.row-expanded {
    background-color: #F1F5F9;
    border-bottom: none;
  }
}

.op-group-row td {
  padding: 14px 16px;
  vertical-align: middle;
}

.op-group-icon-avatar {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #EEF2F6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.op-group-code-pill {
  padding: 4px 10px;
  background: #EEF2F6;
  border-radius: 8px;
  color: #082C69;
  font-size: 13px;
  font-weight: 800;
}

.op-metric-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 8px;
  font-size: 12px;
  color: #334155;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
}

.metric-trips {
  background: #F0FDF4;
  border-color: #BBF7D0;
  color: #166534;

  &.metric-trips-filtered {
    background: #FFFBEB;
    border-color: #FDE68A;
    color: #92400E;
  }
}

.op-group-count-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  background: #EEF2F6;
  color: #082C69;
  border: 1px solid #CBD5E1;

  &.pill-filtered {
    background: #FFFBEB;
    color: #B45309;
    border-color: #F59E0B;
  }
}

/* Per-Group Compact Filter Bar */
.op-group-filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 8px 12px;
}

.op-group-filter-inputs {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1 1 auto;
}

.op-gf-item {
  min-width: 150px;
}

.op-gf-date {
  width: 170px;
}

.op-gf-route {
  min-width: 220px;
  flex: 1 1 220px;
  max-width: 320px;
}

.op-gf-status {
  min-width: 185px;
  width: 200px;
}

::v-deep .op-gf-control .v-input__control {
  min-height: 34px !important;
}

::v-deep .op-gf-control .v-input__slot {
  min-height: 34px !important;
  padding: 0 10px !important;
  border-radius: 8px !important;
  background: #F8FAFC !important;
  font-size: 12px !important;
}

::v-deep .op-gf-control .v-label {
  font-size: 11px !important;
  top: 7px !important;
}

::v-deep .op-gf-control .v-label--active {
  transform: translateY(-14px) scale(0.85) !important;
}

.op-gf-reset-btn {
  display: inline-flex;
  align-items: center;
  height: 34px;
  padding: 0 12px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  color: #64748B;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #F1F5F9;
    border-color: #CBD5E1;
    color: #082C69;
  }

  &.is-active {
    background: rgba(245, 158, 11, 0.12);
    border-color: #F59E0B;
    color: #082C69;
    font-weight: 700;
  }
}

.metric-routes {
  background: #F0F9FF;
  border-color: #BAE6FD;
  color: #0369A1;
}

.op-btn-expand {
  height: 32px !important;
  background: #FFFFFF !important;
  border: 1px solid #CBD5E1 !important;
  border-radius: 8px !important;
  color: #082C69 !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  padding: 0 12px !important;
  transition: all 0.2s ease;

  &:hover {
    background: #082C69 !important;
    color: #FFFFFF !important;
    border-color: #082C69 !important;

    .op-chevron-icon {
      color: #FFFFFF !important;
    }
  }

  &.btn-expanded {
    background: #082C69 !important;
    color: #FFFFFF !important;
    border-color: #082C69 !important;

    .op-chevron-icon {
      color: #FFFFFF !important;
    }
  }
}

/* Nested Trips Row */
.op-nested-trips-tr td {
  border-bottom: 2px solid #CBD5E1;
}

.op-nested-container {
  background: #F8FAFC;
  border-top: 1px dashed #CBD5E1;
}

.op-nested-title {
  font-size: 15px;
  font-weight: 700;
  color: #082C69;
}

.op-trips-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.op-trips-table th {
  padding: 10px 14px;
  background-color: #F1F5F9;
  color: #475569;
  font-weight: 700;
  text-align: right;
  border-bottom: 1px solid #E2E8F0;
  white-space: nowrap;
}

.op-trips-table td {
  padding: 10px 14px;
  border-bottom: 1px solid #F1F5F9;
  vertical-align: middle;
}

.op-trip-item-row:hover td {
  background-color: #F8FAFC;
}

.op-trip-code-pill {
  display: inline-block;
  padding: 2px 7px;
  background: #EEF2F6;
  border-radius: 6px;
  color: #082C69;
  font-size: 12px;
}

.op-parent-ref {
  font-size: 10px;
  color: #64748B;
}

.op-route-code-pill {
  display: inline-block;
  padding: 1px 6px;
  background: #F1F5F9;
  border-radius: 4px;
  color: #64748B;
  font-size: 11px;
}

/* Driver Select Cell */
.op-driver-select-cell {
  min-width: 220px;
}

.op-driver-select {
  font-size: 12px !important;
}

::v-deep .op-driver-select .v-input__control {
  min-height: 34px !important;
}

::v-deep .op-driver-select .v-input__slot {
  min-height: 34px !important;
  padding: 0 8px !important;
  border-color: #CBD5E1 !important;
  background: #FFFFFF !important;
}

::v-deep .op-driver-select .v-select__selection {
  font-size: 12px !important;
  font-weight: 600;
  color: #082C69 !important;
}

/* Inline Driver Fare Input */
.op-fare-cell {
  min-width: 140px;
}

.op-fare-input-box {
  display: flex;
  align-items: center;
  background: #FFFFFF;
  border: 1px solid #CBD5E1;
  border-radius: 8px;
  padding: 0 10px;
  height: 34px;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: #082C69 !important;
    box-shadow: 0 0 0 2px rgba(8, 44, 105, 0.1);
  }

  &.is-disabled {
    background: #F1F5F9 !important;
    border-color: #E2E8F0 !important;

    .op-fare-input {
      color: #64748B;
      cursor: not-allowed;
      background: transparent;
    }

    .op-fare-currency {
      color: #94A3B8;
    }
  }

  &.is-loading {
    opacity: 0.7;
  }
}

.op-fare-input {
  width: 100%;
  border: none;
  outline: none;
  font-size: 13px;
  font-weight: 700;
  color: #082C69;
  font-family: inherit;
  direction: ltr;
  text-align: right;
  background: transparent;

  &::placeholder {
    color: #94A3B8;
    font-weight: 400;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
}

.op-fare-currency {
  font-size: 11px;
  font-weight: 700;
  color: #64748B;
  margin-right: 6px;
  white-space: nowrap;
  user-select: none;
}

.op-fare-spinner {
  margin-right: 4px;
}

/* Badges */
.op-status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
}

.badge-published {
  background-color: #ECFDF5;
  color: #065F46;
  border: 1px solid #A7F3D0;
}

.badge-draft {
  background-color: #FEF3C7;
  color: #92400E;
  border: 1px solid #FDE68A;
}

.badge-inactive {
  background-color: #F1F5F9;
  color: #475569;
  border: 1px solid #CBD5E1;
}

.op-trip-status-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
}

.status-assigned {
  background: #ECFDF5;
  color: #065F46;
  border: 1px solid #A7F3D0;
}

.status-unassigned {
  background: #FFFBEB;
  color: #B45309;
  border: 1px solid #FDE68A;
}

.status-running {
  background: #EFF6FF;
  color: #1D4ED8;
  border: 1px solid #BFDBFE;
}

.status-completed {
  background: #F1F5F9;
  color: #475569;
  border: 1px solid #CBD5E1;
}

.status-cancelled {
  background: #FEF2F2;
  color: #991B1B;
  border: 1px solid #FECACA;
}

.op-action-btn {
  background: #F1F5F9 !important;
  border-radius: 8px !important;

  &:hover {
    background: #E2E8F0 !important;
  }
}

/* Modal */
.op-dialog-card {
  border-radius: 14px;
}

.op-dialog-header {
  background: #FAFCFE;
}

.op-icon-header-box {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #EEF2F6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.op-dialog-title {
  font-size: 16px;
  font-weight: 700;
  color: #082C69;
}

.op-trip-modal-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
}

.op-modal-stat-card {
  padding: 12px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
}

.op-modal-stat-card .stat-label {
  font-size: 11px;
  color: #64748B;
  margin-bottom: 4px;
}

.op-modal-stat-card .stat-value {
  font-size: 14px;
  font-weight: 700;
}

.op-modal-map-container {
  height: 280px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #E2E8F0;
}

.op-map-element {
  width: 100%;
  height: 100%;
}

.op-btn-secondary {
  background: #FFFFFF !important;
  border: 1px solid #E2E8F0 !important;
  color: #475569 !important;
}

/* Colors & Helpers */
.text-navy { color: #082C69 !important; }
.text-emerald { color: #059669 !important; }
.text-amber { color: #D97706 !important; }
.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }

/* Empty state */
.op-unassigned-card {
  background: #FFFFFF;
  border: 1px dashed #CBD5E1;
  border-radius: 20px;
}

.op-empty-icon-circle {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: #F1F5F9;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Unassigned Trips Alert */
.op-unassigned-alert-card {
  background: #FFFBEB;
  border: 1px solid #FCD34D;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(245, 158, 11, 0.08);
}

.op-alert-icon-box {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  background: #FEF3C7;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.op-unassigned-table {
  width: 100%;
  border-collapse: collapse;
}

.op-unassigned-table th {
  background: #FEF3C7;
  color: #92400E;
  font-size: 12px;
  font-weight: 700;
  padding: 10px 14px;
  text-align: right;
  border-bottom: 1px solid #FDE68A;
}

.op-unassigned-table td {
  padding: 10px 14px;
  font-size: 13px;
  color: #1E293B;
  border-bottom: 1px solid #FEF3C7;
  vertical-align: middle;
}

.op-unassigned-table tr:hover {
  background: #FEF9C3;
}

.op-empty-title {
  font-size: 19px;
  font-weight: 700;
  color: #1E293B;
}

.op-empty-desc {
  max-width: 520px;
  font-size: 13px;
  color: #64748B;
  line-height: 1.6;
}
</style>
